import { useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Report, ReportStatus } from 'dashboard/models/report';
import { showMessage } from 'dashboard/redux/actions/messages';
import { clearReport } from 'dashboard/redux/actions/report';
import { RootState } from 'dashboard/redux/store';
import {
  loadReport,
  pauseReport,
  printReport,
  redactReport,
  resumeReport,
  runReportAdhoc,
} from 'dashboard/redux/thunk-actions/report';
import { DashboardReportRunsShowView } from 'dashboard/views/DashboardReportRunsShowView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useInterval from 'lib/hooks/useInterval';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import useRedirectOnEnvironmentChange from 'lib/hooks/useRedirectOnEnvironmentChange';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { captureException } from 'lib/reporting';

type Params = { reportId: string; reportRunId: string };

const DashboardReportRunsShowRoute = (props: PropsFromRedux) => {
  const { report } = props;
  const [isPollRunning, setIsPollRunning] = useState(true);
  const params = useParams<Params>() as Params;

  useOnMountAndUnmount(props.clearReport);

  useRedirectOnEnvironmentChange('/dashboard/reports');

  const { loading, callback: loadReport } = useApiLoader({
    loader: () => props.loadReport(params.reportId, props.currentEnvironment),
    deps: [props.loadReport, params.reportId],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load report.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: pollReport, error: pollReportError } = useApiCallback(async () => {
    if (props.report?.attributes?.status === ReportStatus.Pending) {
      props.loadReport(params.reportId, props.currentEnvironment);
    } else {
      setIsPollRunning(false);
    }
  }, [props.report, props.loadReport, params.reportId]);

  useApiErrorHandler({
    error: pollReportError,

    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to refresh report.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues.', MessageType.Failure),
  });

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  useInterval({ callback: pollReport, delay: isPollRunning ? 5000 : null });

  const { callback: handleUpdateTags, error: handleUpdateTagsError } = useApiCallback(
    async ({ tags: tagIds }: FormUpdateTagsData) => {
      await SessionClientDashboard.postResourceAction<Report>(
        {
          type: ApiModel.Report,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          id: props.report.id,
        },
        'set-tags',
        { meta: { 'tag-id': tagIds } },
      );

      await loadReport();
    },
    [props.report, props.reportTags, loadReport],
  );

  useApiErrorHandler({
    error: handleUpdateTagsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        captureException(error);
        props.showMessage('Failed to add tags to report.', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage('Failed to add tags to report.', MessageType.Failure),
  });

  if (report == null && loading) {
    return (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    );
  }

  const reportRun = props.reportRuns.find((run) => run.id === params.reportRunId);

  // Only custom lists are supported for report runs
  if ((report != null && report.type !== ApiModel.ReportCustomList) || reportRun == null) {
    return (
      <Backdrop>
        <NullState message='We couldn’t find that Report Run.' backRoute='/dashboard/reports' />
      </Backdrop>
    );
  }

  return (
    <DashboardReportRunsShowView
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      report={props.report}
      reportRun={reportRun}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      reportTemplate={props.reportTemplate}
      reportTags={props.reportTags}
      cases={props.cases}
      users={props.users}
      relatedReports={props.relatedReports}
      handleUpdateTags={handleUpdateTags}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    report: state.report.report,
    reportRuns: state.reportRuns.reportRuns[state.currentEnvironment.currentEnvironment.id] || [],
    reportTemplate: state.report.reportTemplate,
    reportTags: state.report.tags,
    cases: state.report.cases,
    users: state.user.users,
    relatedReports: state.report.relatedReports,
  };
};

const mapDispatchToProps = {
  clearReport,
  loadReport,
  printReport,
  showMessage,
  pauseReport,
  redactReport,
  resumeReport,
  runReportAdhoc,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardReportRunsShowRoute);
